import { CommunicationSizeComponent, ComponentIds, HierarchyUtils } from '@inter-ikea-core/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { Product } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getMarketProducts, getSelectedProduct } from 'ducks/products';
import * as R from 'ramda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import Box from 'ui/Box';
import Title from 'ui/Typography/Title';
import { useBehaviorTracker } from 'contexts/behaviors';
import SegmentedControl from '@ingka/segmented-control';

export default function ShelfDepth() {
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const products = useSelector(getMarketProducts);
  const selectedShelf = useSelector(getSelectedProduct);
  const behaviorTracker = useBehaviorTracker();
  const selectedEntity = selectedShelf ? coreApi.entities.find(selectedShelf.entityId) : null;
  const frame = selectedEntity
    ? HierarchyUtils.findWithComponentInUpwardsHierarchy(
        selectedEntity,
        ComponentIds.FRAME_COMPONENT_ID
      )
    : null;
  const frameSize = frame
    ? frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size
    : null;

  const shelfInOtherDepth = products.find(
    p =>
      p.type === selectedShelf?.type &&
      p.communicatedDimensions.depth !== selectedShelf?.communicatedDimensions.depth &&
      p.design === selectedShelf?.design
  );

  if (!selectedEntity || !selectedShelf || !shelfInOtherDepth || frameSize?.depth !== 400) {
    return null;
  }

  const sortedShelves: Product[] = R.sortWith([
    R.ascend(R.path(['communicatedDimensions', 'depth']))
  ])([selectedShelf, shelfInOtherDepth]);

  const items = sortedShelves.map(shelf => ({
    label: `${shelf.communicatedDimensionsLocalized.depth} ${shelf.communicatedDimensionsLocalized.unit}`,
    icon: ''
  }));

  const selectedIndex = sortedShelves.findIndex(
    shelf => shelf.communicatedDimensions.depth === selectedShelf.communicatedDimensions.depth
  );

  const onToggleSelect = (index: number) => {
    const meta = {
      size: { dimension: 'depth', value: sortedShelves[index].communicatedDimensions.depth }
    };

    behaviorTracker.withMeta(meta).custom(() => {
      if (index !== selectedIndex) {
        const newShelf = sortedShelves[index];
        const replacementResult = coreApi.products.replace(
          selectedEntity.id,
          newShelf.coreProductId
        );
        if (replacementResult?.replacedEntityResult.placedEntity) {
          coreApi.entities.select(replacementResult?.replacedEntityResult.placedEntity);
        }
      }
    });
  };

  return (
    <Box mt="l" mb="l">
      <Title type={ThemeFontStyleTypeEnum.headingXSmall} mb="md">
        {formatMessage({ id: 'storageone.depth' })}
      </Title>
      <SegmentedControl
        data-testid="front-type-toggle"
        items={items}
        onClick={(_e, value) => onToggleSelect(value)}
        activeIndex={selectedIndex}
      />
    </Box>
  );
}
